import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Ban,
  Clock,
  FileMinus,
  FilePlus,
} from 'lucide-react';
import type {
  DashboardToday,
  DashboardTodayInvoice,
  DashboardUrgentTask,
} from '../../lib/dashboardApi';
import { formatCurrency } from '../../lib/format';
import { TaskListRow } from '../tasks/TaskListRow';

interface TodayPanelProps {
  today: DashboardToday;
}

function InvoiceRow({
  invoice,
  tone,
}: {
  invoice: DashboardTodayInvoice;
  tone: 'income' | 'outcome';
}) {
  const accent = tone === 'income' ? 'text-emerald-400' : 'text-red-400';
  return (
    <li className="flex items-center justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <p className="truncate text-sm text-foreground">{invoice.party ?? invoice.invoiceNumber}</p>
        <p className="truncate text-xs text-muted-foreground">{invoice.invoiceNumber}</p>
      </div>
      <span className={`shrink-0 text-sm font-semibold ${accent}`}>
        {/* A credit note (negative amount) flips the sign of its side. */}
        {(tone === 'income') === invoice.amount >= 0 ? '+' : '−'}
        {formatCurrency(Math.abs(invoice.amount), invoice.currency)}
      </span>
    </li>
  );
}

function FinancialBlock({
  icon: Icon,
  title,
  invoices,
  tone,
  emptyLabel,
}: {
  icon: typeof ArrowDownLeft;
  title: string;
  invoices: DashboardTodayInvoice[];
  tone: 'income' | 'outcome';
  emptyLabel: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        <span>{title}</span>
        {invoices.length > 0 && (
          <span className="ml-auto rounded-full bg-muted/60 px-2 py-0.5 text-[11px] text-foreground">
            {invoices.length}
          </span>
        )}
      </div>
      {invoices.length === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground/70">{emptyLabel}</p>
      ) : (
        <ul className="mt-1 divide-y divide-border/60">
          {invoices.map((inv) => (
            <InvoiceRow key={inv.id} invoice={inv} tone={tone} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TaskBlock({
  icon: Icon,
  title,
  iconClass,
  tasks,
  tone,
  emptyLabel,
}: {
  icon: typeof AlertTriangle;
  title: string;
  iconClass: string;
  tasks: DashboardUrgentTask[];
  tone: 'overdue' | 'critical' | 'blocked';
  emptyLabel: string;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-card">
      <div className="flex h-8 items-center gap-2 border-b border-gray-200 px-3 text-[13px]">
        <Icon className={`h-3.5 w-3.5 ${iconClass}`} aria-hidden />
        <span className="font-semibold text-gray-800">{title}</span>
        <span className="text-gray-500">{tasks.length}</span>
      </div>
      {tasks.length === 0 ? (
        <p className="px-3 py-2 text-xs text-gray-400">{emptyLabel}</p>
      ) : (
        <ul>
          {tasks.map((t) => (
            <TaskListRow key={t.id} task={t} tone={tone} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * "Azi" panel (CLE-256, Secțiunea 2). Two columns: financial movements due/issued
 * today and the urgent-task buckets (overdue, critical, blocked/needs-action).
 */
export function TodayPanel({ today }: TodayPanelProps) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-xl border border-border bg-card p-4 shadow-card sm:p-5">
        <h3 className="text-sm font-semibold text-foreground">Financiar azi</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <FinancialBlock
            icon={ArrowDownLeft}
            title="Încasări planificate"
            invoices={today.incomingToday}
            tone="income"
            emptyLabel="Nicio încasare scadentă azi"
          />
          <FinancialBlock
            icon={ArrowUpRight}
            title="Plăți planificate"
            invoices={today.outgoingToday}
            tone="outcome"
            emptyLabel="Nicio plată scadentă azi"
          />
          <FinancialBlock
            icon={FilePlus}
            title="Facturi emise azi"
            invoices={today.invoicesIssuedToday}
            tone="income"
            emptyLabel="Nicio factură emisă azi"
          />
          <FinancialBlock
            icon={FileMinus}
            title="Facturi primite azi"
            invoices={today.invoicesReceivedToday}
            tone="outcome"
            emptyLabel="Nicio factură primită azi"
          />
        </div>
      </section>

      <section className="min-w-0 rounded-xl border border-border bg-card p-4 shadow-card sm:p-5">
        <h3 className="text-sm font-semibold text-foreground">Task-uri & urgențe</h3>
        <div className="mt-4 space-y-4">
          <TaskBlock
            icon={Clock}
            iconClass="text-red-400"
            title="Întârziate"
            tasks={today.overdueTasks}
            tone="overdue"
            emptyLabel="Niciun task întârziat"
          />
          <TaskBlock
            icon={AlertTriangle}
            iconClass="text-amber-400"
            title="Critice"
            tasks={today.criticalTasks}
            tone="critical"
            emptyLabel="Niciun task critic"
          />
          <TaskBlock
            icon={Ban}
            iconClass="text-slate-400"
            title="Blocate / necesită acțiune"
            tasks={today.blockedTasks}
            tone="blocked"
            emptyLabel="Niciun task blocat"
          />
        </div>
      </section>
    </div>
  );
}
